/**
 * Minimal Wavefront OBJ parser — just enough for LEGO voxelization.
 * Supports `v` (vertices) and `f` (faces, fan-triangulated, 1-based or
 * negative-relative indices, `i`, `i/j`, `i//k`, `i/j/k` forms). All other
 * lines (o, g, usemtl, mtllib, s, vn, vt, ...) are ignored.
 */

export interface ObjMesh {
  /** [x, y, z] triplets */
  vertices: Array<[number, number, number]>;
  /** Triangles as triples of vertex indices (0-based, post-triangulation) */
  triangles: Array<[number, number, number]>;
  /** Axis-aligned bounds, computed from the vertices */
  bounds: {
    min: [number, number, number];
    max: [number, number, number];
  };
}

const parseVertex = (parts: string[]): [number, number, number] | null => {
  const x = Number(parts[0]);
  const y = Number(parts[1]);
  const z = Number(parts[2]);
  if (!Number.isFinite(x) || !Number.isFinite(y) || !Number.isFinite(z)) {
    return null;
  }
  return [x, y, z];
};

/** Resolve an OBJ face index (1-based positive or negative-relative). */
const resolveIndex = (raw: string, vertexCount: number): number => {
  const i = Number(raw);
  if (!Number.isInteger(i) || i === 0) return -1;
  if (i > 0) return i - 1 < vertexCount ? i - 1 : -1;
  return vertexCount + i >= 0 ? vertexCount + i : -1;
};

export const parseObj = (source: string): ObjMesh | null => {
  const vertices: Array<[number, number, number]> = [];
  const faces: number[][] = [];

  for (const rawLine of source.split(/\r?\n/)) {
    const line = rawLine.trim();
    if (!line || line.startsWith("#")) continue;
    const parts = line.split(/\s+/);
    const keyword = parts[0];

    if (keyword === "v") {
      const v = parseVertex(parts.slice(1));
      if (v) vertices.push(v);
    } else if (keyword === "f") {
      const indices = parts
        .slice(1)
        .map((token) => resolveIndex(token.split("/")[0], vertices.length))
        .filter((i) => i >= 0);
      if (indices.length >= 3) faces.push(indices);
    }
  }

  if (vertices.length === 0 || faces.length === 0) return null;

  // Fan-triangulate polygons
  const triangles: Array<[number, number, number]> = [];
  for (const face of faces) {
    for (let i = 1; i < face.length - 1; i++) {
      triangles.push([face[0], face[i], face[i + 1]]);
    }
  }

  const min: [number, number, number] = [Infinity, Infinity, Infinity];
  const max: [number, number, number] = [-Infinity, -Infinity, -Infinity];
  for (const [x, y, z] of vertices) {
    min[0] = Math.min(min[0], x);
    min[1] = Math.min(min[1], y);
    min[2] = Math.min(min[2], z);
    max[0] = Math.max(max[0], x);
    max[1] = Math.max(max[1], y);
    max[2] = Math.max(max[2], z);
  }

  return { vertices, triangles, bounds: { min, max } };
};
